import React from "react";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import {
  CHECKLIST_ITEMS,
  confirmationFlag,
  formatConfirmDate,
  formatConfirmRole,
  getChecklistEntry,
  isFlagOn,
  isSystemActor,
} from "../helpers/confirmationAudit";
import "../assets/css/ConfirmationAuditModal.css";

const formatRetreatDates = (retreat) => {
  if (!retreat?.dateFrom) return "";
  const from = moment(retreat.dateFrom).tz("America/New_York");
  const to = moment(retreat.dateTo || retreat.dateFrom).tz("America/New_York");
  if (from.isSame(to, "day")) return from.format("MMM D, YYYY");
  return `${from.format("MMM D")} – ${to.format("MMM D, YYYY")}`;
};

const PersonValue = ({ name, email, fallback = "—" }) => {
  if (!name) {
    return <span className="ca-muted">{fallback}</span>;
  }
  if (!email) {
    return <span className="ca-value">{name}</span>;
  }
  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 200, hide: 150 }}
      overlay={
        <Tooltip className="confirm-audit-tooltip">
          <div className="confirm-audit-tip">
            <strong>{name}</strong>
            <span>{email}</span>
          </div>
        </Tooltip>
      }
    >
      <span className="ca-person">{name}</span>
    </OverlayTrigger>
  );
};

const ActorBlock = ({
  name,
  email,
  role,
  coupleId,
  date,
  emptyLabel = "—",
}) => {
  const system = isSystemActor(name, role);
  const displayName = system ? "Automatic (system)" : name;
  return (
    <div className="ca-rows">
      <div className="ca-row">
        <span className="ca-label">By</span>
        <span className="ca-value">
          <PersonValue
            name={displayName}
            email={system ? "" : email}
            fallback={emptyLabel}
          />
        </span>
      </div>
      <div className="ca-row">
        <span className="ca-label">Role</span>
        <span className="ca-value">
          {system
            ? "Automatic (system)"
            : formatConfirmRole(role) || <span className="ca-muted">—</span>}
        </span>
      </div>
      {coupleId && !system ? (
        <div className="ca-row">
          <span className="ca-label">Couple ID</span>
          <span className="ca-value">{coupleId}</span>
        </div>
      ) : null}
      <div className="ca-row">
        <span className="ca-label">Date</span>
        <span className="ca-value">
          {formatConfirmDate(date) || <span className="ca-muted">—</span>}
        </span>
      </div>
    </div>
  );
};

const ConfirmationAuditModal = ({ show, retreat, onClose }) => {
  const navigate = useNavigate();
  const closed =
    isFlagOn(retreat?.retreatCloseStatus) ||
    isFlagOn(retreat?.confirmedByAdmin);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      className="ca-modal"
      dialogClassName="ca-dialog"
      contentClassName="ca-content"
    >
      <div className="ca-shell">
        <div className="ca-header">
          <div className="ca-mark">
            <i className="mdi mdi-clipboard-check-outline" />
          </div>
          <div className="ca-header-copy">
            <p className="ca-kicker">Confirmation record</p>
            <h2>
              {retreat?.id ? (
                <a
                  href={`/retreatRoster?retreatId=${retreat.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    onClose();
                    navigate(`/retreatRoster?retreatId=${retreat.id}`);
                  }}
                >
                  {retreat?.displayTitle ||
                    retreat?.title ||
                    "Untitled Retreat"}
                </a>
              ) : (
                retreat?.displayTitle ||
                retreat?.title ||
                "Untitled Retreat"
              )}
            </h2>
            <p className="ca-dates">{formatRetreatDates(retreat)}</p>
          </div>
        </div>

        <div className="ca-body">
          <div className="ca-grid">
            {CHECKLIST_ITEMS.map((item) => {
              const entry = getChecklistEntry(retreat, item.key);
              const confirmed = confirmationFlag(retreat, item.key);
              return (
                <article className="ca-card" key={item.key}>
                  <div className="ca-card-top">
                    <h3 className="ca-card-title">{item.title}</h3>
                    <span
                      className={`ca-badge ${confirmed ? "is-on" : "is-off"}`}
                    >
                      {confirmed ? "Confirmed" : "Not confirmed"}
                    </span>
                  </div>
                  <ActorBlock
                    name={entry?.confirmedByName}
                    email={entry?.confirmedByEmail}
                    role={entry?.confirmedByRole}
                    coupleId={entry?.confirmedByCoupleId}
                    date={entry?.ConfirmedDate}
                    emptyLabel="Not recorded"
                  />
                  {entry?.unconfirmedByName ? (
                    <div className="ca-unconfirm">
                      <div className="ca-card-top">
                        <h3 className="ca-card-title">Last unconfirmed</h3>
                      </div>
                      <ActorBlock
                        name={entry.unconfirmedByName}
                        email={entry.unconfirmedByEmail}
                        role={entry.unconfirmedByRole}
                        coupleId={entry.unconfirmedByCoupleId}
                        date={entry.UnconfirmedDate}
                      />
                    </div>
                  ) : null}
                </article>
              );
            })}
            <article className="ca-card is-closed">
              <div className="ca-card-top">
                <h3 className="ca-card-title">Retreat Validated and Closed</h3>
                <span className={`ca-badge ${closed ? "is-on" : "is-off"}`}>
                  {closed ? "Closed" : "Open"}
                </span>
              </div>
              <div className="ca-rows">
                <div className="ca-row">
                  <span className="ca-label">Status</span>
                  <span className="ca-value">
                    {closed
                      ? isFlagOn(retreat?.confirmedByAdmin)
                        ? "Validated and closed by Super Admin"
                        : "Validated and closed"
                      : "Awaiting Super Admin validation"}
                  </span>
                </div>
              </div>
            </article>
          </div>
        </div>

        <div className="ca-footer">
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationAuditModal;
